import { jsonToState } from 'atpjs/helpers/json'
import i18n from 'atpjs/helpers/i18n'
import {
  otpGenerate as otpGenerateUrl,
  otpVerify as otpVerifyUrl,
  getLead as getLeadUrl,
  downloadDocument
} from 'atpjs/api/atp'
import { responseToJson, handleStatus, fetchAuthHeader, fetchApi } from 'atpjs/api'
import { replaceWithObject } from 'atpjs/helpers/string'
import { formatDate } from 'tve/helpers/date'
import mapping, { topicsMapping } from 'tve/stores/mapping'
// import { appointmentMockData } from 'tve/data/mock/appointment'
import { topicsBySubject } from 'tve/helpers/logic'
import { getEnv } from 'tve/helpers/env'
import { requestUrls } from 'tve/config/requests'

const formatISO8601 = (date) => {
  if (date == null) return ''
  if (typeof date === 'string') return date
  return date.toISOString()
}

const urlWithHost = (key) => {
  return requestUrls(getEnv('API_HOST'))[key]
}

export const getLead = (id) => getLeadUrl(urlWithHost('apiGetLead'), mapping)(id)

export const getBranch = ({ zip, topicIds, locale }) => {
  const parts = []
  if (zip) parts.push(`zip=${encodeURIComponent(zip)}`)
  if (topicIds) parts.push(topicIds.map((id) => `topic_ids[]=${encodeURIComponent(id)}`).join('&'))
  if (locale) parts.push(`locale=${encodeURIComponent(locale)}`)
  return fetchApi(`${urlWithHost('apiGetBranch')}?${parts.join('&')}`, 'GET')
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState({}))
}

let globalTopics = null
export const getTopics = () => {
  if (globalTopics) return globalTopics
  throw new Error('globalTopics not set')
}

export const loadTopics = () => {
  return fetchApi(urlWithHost('apiGetTopics'), 'GET')
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState(topicsMapping))
    .then((topics) => (globalTopics = topics))
}

export const getClient = (identifier, search = null) => {
  const replace = replaceWithObject({
    identifier: encodeURIComponent(identifier.toUpperCase())
  })

  if (search) {
    search = `?${search}`
  } else {
    search = ''
  }

  return fetchApi(replace(urlWithHost('apiClient')) + search, 'GET')
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState({}))
}

export const loadSlots = (date, locale, medium, topicIds, branchCode, email, street, zip, city) => {
  const searchParams = new URLSearchParams()
  if (medium) searchParams.set('medium', medium)
  if (locale) searchParams.set('language', locale)
  if (date) searchParams.set('start_date', formatDate(date))
  if (branchCode) searchParams.set('branch_code', branchCode)
  if (street) searchParams.set('street', street)
  if (zip) searchParams.set('zip', zip)
  if (city) searchParams.set('city', city)
  if (email) searchParams.set('user_email', email)
  topicIds.forEach((id) => {
    searchParams.append('topic_ids[]', id)
  })
  return getAppointmentSlots(searchParams.toString())
}

// http://localhost:5000/api/tve/slots?language=de&medium=in_person&start_date=2017-08-24&appointment_type_code=PUNDS&branch_code=LZE
export const getAppointmentSlots = (params) => {
  const replace = replaceWithObject({
    params: params
  })

  return fetchApi(replace(urlWithHost('apiGetAppointmentSlots')), 'GET')
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState({}))
}

export const getUpdateAppointmentSlots = (id, startDate) => {
  const replace = replaceWithObject({
    id: encodeURIComponent(id),
    startDate: encodeURIComponent(formatDate(startDate))
  })

  return fetchApi(replace(urlWithHost('apiUpdateAppointmentSlots')), 'GET')
    .then(handleStatus)
    .then(responseToJson)
  // .catch(appointmentMockData)
}

const dataToCreateBody = (data) => {
  const startDate = data.getIn(['event', 'start'])

  return {
    appointment: {
      user_email: data.getIn(['advisor', 'email']),
      medium: data.get('medium'),
      language: data.get('locale'),
      topic_ids: topicsBySubject(data).map((topic) => topic.get('id')),
      branch_code: data.getIn(['branch', 'code']),
      start_time: formatISO8601(startDate),
      street: data.getIn(['appointment', 'street']),
      zip: data.getIn(['appointment', 'zip']),
      city: data.getIn(['appointment', 'city']),
      first_name: data.get('firstName'),
      last_name: data.get('lastName'),
      email: data.get('email'),
      mobile_number: data.get('phone')
    },
    locale: i18n.locale,
    existing_client: data.get('login'),
    business_client: data.get('customerType') == 'corporate',
    company_name: data.get('company'),
    product: 'tve_appointment',
    gender: data.get('gender'),
    first_name: data.get('firstName'),
    last_name: data.get('lastName'),
    mobile_number: data.get('phone'),
    email: data.get('email'),
    street: data.get('street'),
    zip: data.get('zip'),
    city: data.get('city'),
    birth_date: data.get('birthDate'),
    comment: data.get('comment'),
    ...(data.get('iban') && { iban: data.get('iban') }),
    ...(data.get('contractNumber') && { contract_number: data.get('contractNumber') })
  }
}

const dataToUpdateBody = (data) => {
  const date = data.getIn(['event', 'start'])
  return {
    start_time: formatISO8601(date)
  }
}

export const pushLead = (id, data, topics, _dataHandler) => {
  if (id) {
    return updateLead(id, data)
  } else {
    return createLead(data, topics)
  }
}

export const createLead = (data, topics) => {
  const body = dataToCreateBody(data, topics)

  return fetchApi(urlWithHost('apiCreateLead'), 'POST', JSON.stringify(body))
    .then(handleStatus)
    .then(responseToJson)
    .then(fetchAuthHeader)
    .then(jsonToState(mapping))
}

export const updateLead = (id, data) => {
  const body = dataToUpdateBody(data)
  const replace = replaceWithObject({ id: encodeURIComponent(id) })

  return fetchApi(replace(urlWithHost('apiPushLead')), 'PUT', JSON.stringify(body))
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState(mapping))
}

export const cancelLead = (data) => {
  const id = data.get('id')
  const replace = replaceWithObject({ id: encodeURIComponent(id) })
  const body = {
    cancellation_reason: data.get('cancellationReason'),
    cancellation_comment: data.get('cancellationComment')
  }

  return fetchApi(replace(urlWithHost('apiCancelLead')), 'POST', JSON.stringify(body))
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState(mapping))
}

export const fetchDocuments = (id) => {
  const replace = replaceWithObject({ lead_id: encodeURIComponent(id) })

  return fetchApi(replace(urlWithHost('apiLeadDocuments')), 'GET')
    .then(handleStatus)
    .then(responseToJson)
}

export const advisorByEmail = (email) => {
  const replace = replaceWithObject({ email: encodeURIComponent(email) })
  return fetchApi(replace(urlWithHost('apiAdvisorByEmail')), 'GET')
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState(mapping))
}

/**
 * send sms to saved phoneNumber, phoneNumber is returned
 * @param {string} id   lead id
 * @return {CancelablePromise} {number}
 */
export const otpGenerate = (id) => otpGenerateUrl(urlWithHost('apiOtpGenerate'))(id)

/**
 * fetch auth in response
 * @param {string} id   lead id
 * @param {string} pin
 * @return {CancelablePromise}
 */
export const otpVerify = (id, pin) => otpVerifyUrl(urlWithHost('apiOtpVerify'))(id, pin)

/**
 * meta: { step, field }
 */
export const uploadDocument = (id) => (file, meta = {}) => {
  const formData = new FormData()
  formData.append('file', file)
  for (const key in meta) {
    formData.append(key, meta[key])
  }
  const replace = replaceWithObject({ lead_id: id })
  return fetchApi(replace(urlWithHost('apiLeadDocuments')), 'POST', formData)
    .then(handleStatus)
    .then(responseToJson)
}

export const deleteDocument = (id, docId) => {
  const replace = replaceWithObject({ lead_id: id, docId: docId })
  return fetchApi(replace(urlWithHost('apiLeadDocumentDelete')), 'DELETE').then(handleStatus)
}

export { downloadDocument }
